import React, { useState, useRef } from 'react';
import { X, Palette, Upload, Trash2, Loader2, CheckCircle, Eye } from 'lucide-react';
import api from '../api';
import Toast from './Toast';

const COLOR_PRESETS = [
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Green', value: '#10b981' },
  { name: 'Teal', value: '#14b8a6' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Dark', value: '#1e293b' },
];

const POSITION_OPTIONS = [
  { name: 'Bottom Right', value: 'bottom-right' },
  { name: 'Bottom Left', value: 'bottom-left' },
  { name: 'Top Right', value: 'top-right' },
  { name: 'Top Left', value: 'top-left' },
];

const SIZE_OPTIONS = [
  { name: 'Small', value: 60 },
  { name: 'Medium', value: 72 },
  { name: 'Large', value: 88 },
  { name: 'Extra Large', value: 104 },
];

const ColorPicker = ({ label, fieldName, value, onChange, pickerId }) => (
  <div className="space-y-3">
    <label className="text-sm font-semibold text-foreground">{label}</label>
    <div className="grid grid-cols-5 gap-2">
      {COLOR_PRESETS.map((c) => (
        <button
          key={c.value}
          type="button"
          onClick={() => onChange(fieldName, c.value)}
          title={c.name}
          className={`flex flex-col items-center gap-1.5 p-2.5 rounded-lg border-2 transition-all ${
            value === c.value
              ? 'border-primary bg-primary/5'
              : 'border-border hover:border-primary/50'
          }`}
        >
          <div className="w-7 h-7 rounded-full" style={{ backgroundColor: c.value }} />
          <span className="text-[10px] text-foreground">{c.name}</span>
          {value === c.value && <CheckCircle className="w-3 h-3 text-primary" />}
        </button>
      ))}
    </div>
    <div className="bg-muted/30 border border-border/60 rounded-xl p-4 flex items-center gap-4">
      <div className="flex flex-col gap-1">
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide">Picker</span>
        <label htmlFor={pickerId} className="relative cursor-pointer group">
          <div
            className="w-16 h-10 rounded-lg border-2 border-white shadow-md group-hover:scale-105 transition-transform ring-2 ring-offset-1 ring-border group-hover:ring-primary"
            style={{ backgroundColor: value }}
          />
          <input
            id={pickerId}
            type="color"
            value={value}
            onChange={(e) => onChange(fieldName, e.target.value)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />
        </label>
      </div>
      <div className="flex-1">
        <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wide block mb-1.5">Hex</span>
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-sm pointer-events-none">#</span>
          <input
            type="text"
            value={value.replace('#', '')}
            onChange={(e) => {
              const v = '#' + e.target.value.replace('#', '');
              if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(fieldName, v);
            }}
            className="w-full pl-7 pr-3 py-2 text-sm font-mono font-semibold bg-background border-2 border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary uppercase"
            maxLength={6}
          />
        </div>
      </div>
    </div>
  </div>
);

const Toggle = ({ checked, onChange, label }) => (
  <div className="flex items-center gap-3">
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
        checked ? 'bg-primary' : 'bg-muted'
      }`}
    >
      <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
        checked ? 'translate-x-5' : 'translate-x-0'
      }`} />
    </button>
    <span className="text-sm text-muted-foreground">{label}</span>
  </div>
);

const LogoVoiceWidgetThemeCustomizer = ({ agent, onClose, onSave }) => {
  const [settings, setSettings] = useState({
    logo_voice_widget_logo_url: agent?.logo_voice_widget_logo_url || null,
    logo_voice_widget_wave_color: agent?.logo_voice_widget_wave_color || '#6366f1',
    logo_voice_widget_button_position: agent?.logo_voice_widget_button_position || 'bottom-right',
    logo_voice_widget_pulse_animation: agent?.logo_voice_widget_pulse_animation ?? true,
    logo_voice_widget_size: agent?.logo_voice_widget_size || 72,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);

  const set = (field, value) => setSettings(prev => ({ ...prev, [field]: value }));
  const toggle = (field) => setSettings(prev => ({ ...prev, [field]: !prev[field] }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await api.put(`/agents/${agent.id}`, settings);
      setToast({ message: 'Logo voice widget settings saved!', type: 'success' });
      setTimeout(() => {
        onSave?.(settings);
        onClose();
      }, 800);
    } catch {
      setToast({ message: 'Failed to save settings', type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/svg+xml'];
    if (!allowed.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|webp|gif|svg)$/i)) {
      setUploadError('Please upload JPG, PNG, WebP, GIF, or SVG.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File too large. Maximum size is 5MB.');
      return;
    }

    setIsUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(`/agents/${agent.id}/logo-voice-widget/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      set('logo_voice_widget_logo_url', response.data.file_url);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setIsUploading(false);
    }
  };

  const handleLogoDelete = async () => {
    try {
      await api.delete(`/agents/${agent.id}/logo-voice-widget/logo`);
      set('logo_voice_widget_logo_url', null);
    } catch (err) {
      setUploadError(err.response?.data?.detail || 'Failed to delete logo');
    }
  };

  const handlePreview = () => {
    const params = new URLSearchParams({
      view: 'button',
      waveColor: settings.logo_voice_widget_wave_color.replace('#', ''),
      position: settings.logo_voice_widget_button_position,
      size: String(settings.logo_voice_widget_size),
    });
    window.open(`${window.location.origin}/embed/logo-voice-widget/${agent.id}?${params}`, '_blank');
  };

  const waveColor = settings.logo_voice_widget_wave_color;
  const logoSize = settings.logo_voice_widget_size;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[10000] flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col shadow-elegant">
        <div className="bg-gradient-secondary text-white p-5 flex justify-between items-center rounded-t-2xl flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-7 h-7 rounded-lg bg-white/20 flex items-center justify-center">
              <Palette className="w-4 h-4" />
            </div>
            <div>
              <h2 className="text-lg font-semibold tracking-tight">Customize Logo Voice Widget</h2>
              <p className="text-white/70 text-xs mt-0.5">Upload your logo, set wave color, and position</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white p-1.5 rounded-lg hover:bg-white/10" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Live preview */}
          <div className="bg-muted/30 border border-border rounded-xl p-6 flex flex-col items-center gap-4">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Preview</span>
            <div style={{ position: 'relative', width: logoSize, height: logoSize }}>
              {settings.logo_voice_widget_pulse_animation && (
                <>
                  {[0, 1, 2].map((i) => (
                    <span
                      key={i}
                      style={{
                        position: 'absolute',
                        inset: 0,
                        borderRadius: '50%',
                        border: `2px solid ${waveColor}66`,
                        animation: 'logoPreviewWave 2.4s ease-out infinite',
                        animationDelay: `${i * 0.8}s`,
                      }}
                    />
                  ))}
                  <style>{`
                    @keyframes logoPreviewWave {
                      0% { transform: scale(1); opacity: 0.55; }
                      100% { transform: scale(2.4); opacity: 0; }
                    }
                  `}</style>
                </>
              )}
              <div
                style={{
                  position: 'relative',
                  zIndex: 2,
                  width: logoSize,
                  height: logoSize,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  boxShadow: `0 4px 16px rgba(0,0,0,0.12), 0 0 0 3px ${waveColor}33`,
                  backgroundColor: '#fff',
                }}
              >
                {settings.logo_voice_widget_logo_url ? (
                  <img
                    src={settings.logo_voice_widget_logo_url}
                    alt="Logo preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `linear-gradient(135deg, ${waveColor}, #818cf8)`,
                      color: '#fff',
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Logo
                  </div>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground text-center">
              Click the logo on your site to start a voice call. A red end button appears beside it during calls.
            </p>
          </div>

          {/* Logo upload */}
          <div className="space-y-3">
            <label className="text-sm font-semibold text-foreground">Company Logo</label>
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml,.jpg,.jpeg,.png,.webp,.gif,.svg"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-voice-upload"
              />
              <label
                htmlFor="logo-voice-upload"
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-lg flex items-center gap-2 text-sm cursor-pointer"
              >
                {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {isUploading ? 'Uploading…' : 'Upload Logo'}
              </label>
              {settings.logo_voice_widget_logo_url && (
                <button
                  type="button"
                  onClick={handleLogoDelete}
                  className="px-3 py-2 text-red-500 hover:bg-red-500/10 rounded-lg flex items-center gap-1.5 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              )}
            </div>
            {uploadError && <p className="text-xs text-red-500">{uploadError}</p>}
            <p className="text-xs text-muted-foreground">Circular crop is applied automatically. JPG, PNG, WebP, GIF, or SVG up to 5MB.</p>
          </div>

          <ColorPicker
            label="Ripple Wave Color"
            fieldName="logo_voice_widget_wave_color"
            value={settings.logo_voice_widget_wave_color}
            onChange={set}
            pickerId="logo-wave-color"
          />

          <div className="space-y-3">
            <label className="text-sm font-semibold text-foreground">Logo Size</label>
            <div className="grid grid-cols-4 gap-2">
              {SIZE_OPTIONS.map(({ name, value }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => set('logo_voice_widget_size', value)}
                  className={`py-2.5 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    settings.logo_voice_widget_size === value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  {name}
                  <span className="block text-[10px] text-muted-foreground mt-0.5">{value}px</span>
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-semibold text-foreground">Position</label>
            <div className="grid grid-cols-2 gap-2">
              {POSITION_OPTIONS.map(({ name, value }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => set('logo_voice_widget_button_position', value)}
                  className={`py-2.5 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                    settings.logo_voice_widget_button_position === value
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
          </div>

          <Toggle
            checked={settings.logo_voice_widget_pulse_animation}
            onChange={() => toggle('logo_voice_widget_pulse_animation')}
            label="Animated ripple waves (attention grabber)"
          />
        </div>

        <div className="border-t border-border p-4 bg-muted/30 flex justify-between items-center flex-shrink-0">
          <button
            type="button"
            onClick={handlePreview}
            className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg flex items-center gap-2 text-sm"
          >
            <Eye className="w-4 h-4" />
            Preview
          </button>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-sm">
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className="px-4 py-2 bg-gradient-button hover:opacity-90 text-primary-foreground rounded-lg text-sm disabled:opacity-50"
            >
              {isSaving ? 'Saving…' : 'Save Settings'}
            </button>
          </div>
        </div>
      </div>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
};

export default LogoVoiceWidgetThemeCustomizer;
